import { injectIntl } from '@edx/frontend-platform/i18n';
import { Button, Container, Spinner, Toast } from '@openedx/paragon';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { deleteComponentBlock, getVerticalBlock, updateVerticleBlock } from './data/api';
import { HTMLComponentCard, SCORMComponentCard } from './components/cards/HTMLComponentCard';
import { VideoComponentCard } from './components/cards/VideoComponentCard';
import { ProblemComponentCard } from './components/cards/ProblemComponentCard';
import { availableComponents,scormComponents } from './data/constant';
import { NoContent } from './components/NoContent';
import { DraggableComponent } from './components/DraggableComponent';
import { AvailableComponentCard } from './components/AvailableComponentCard';
import { UnitContextWrapper } from './data/context/UnitContext';
import { useDispatch, useSelector } from 'react-redux';
import { publishCourseItemQuery } from '../course-outline/data/thunk';
import { getProcessingNotification } from '../generic/processing-notification/data/selectors';
import ProcessingNotification from '../generic/processing-notification';
import SolidSvgComponent from '../_components/solid-svg/SolidSvgComponent';
import ViewIcon from '../assets/images/viewIcon.svg'
import messages from './messages';
import DeleteModal from '../generic/delete-modal/DeleteModal';
import { ToastStatus } from '../data/constants';
import { showToast } from '../generic/custom-toast/data/slice';
import { getCourseAppSettings } from '../advanced-settings/data/selectors';
import { CoursePlugins} from '../helper/constants'
import { actions } from '../editors/data/redux';

const message = {
  video: "Video",
  html: "Text",
  problem: "Question",
  scorm: "SCORM",
}

const Unit = ({ courseId, intl }) => {
  const { unitId ,blockType,blockId} = useParams();
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [components, setComponents] = useState([]);
  const [verticleBlock, setVerticleBlock] = useState(null);
  const [isDeleteBlock, setDeleteBlock] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {advancedModules} = useSelector(getCourseAppSettings);
  const isSCORMEnabled = Boolean(advancedModules && advancedModules?.value && advancedModules?.value?.includes(CoursePlugins.scorm))
    
  const isEditorOpen = useMatch("course/:courseId/container/:unitId/editor/:blockType/:blockId?");


  const handleDeleteComponentBlock = (componentBlockId) => {
    const currentComponent = components.find((component) => component.id === componentBlockId);
    setDeleteBlock(currentComponent)
  };
  const handleDeleteBlock = (id) => {
    try {
      setComponents((components) => components.filter((component) => component.id !== (id || isDeleteBlock?.id)));
      deleteComponentBlock(id || isDeleteBlock?.id);
      !id && dispatch(showToast({
        message: `${message[isDeleteBlock.category]} deleted successfully`,
        status: ToastStatus.SUCCESSFUL
      }));
    } catch (e) {
      console.log("while delete block");

    }
  };

  const selectComponent = (component) => {
    switch (component.category) {
      case 'html':
        return <HTMLComponentCard component={component} onDelete={handleDeleteComponentBlock} />;
      case 'video':
        return <VideoComponentCard component={component} onDelete={handleDeleteComponentBlock} />;
      case 'problem':
        return <ProblemComponentCard component={component} onDelete={handleDeleteComponentBlock} />;
      case 'scorm':
        return <SCORMComponentCard component={component} onDelete={handleDeleteComponentBlock} />;
      default:
        return null;
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );


  const {
    isShow: isShowProcessingNotification,
    title: processingNotificationTitle,
  } = useSelector(getProcessingNotification);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) {
      return;
    }

    const { id } = active;
    const { id: overId } = over;

    const activeIndex = components.findIndex((component) => component.id === id);
    const overIndex = components.findIndex((component) => component.id === overId);

    const currentComponents = [...components];
    const [movedItem] = currentComponents.splice(activeIndex, 1);
    currentComponents.splice(overIndex, 0, movedItem);

    setComponents(currentComponents);

    const newOrder = currentComponents.map((component) => component.id);
    // api call for update children order
    updateVerticleBlock(unitId, {
      children: newOrder,
    });
  };

  const onSuccessComponentBlockCreate = ({ componentBlockCategory, componentBlockId }) => {
    if(componentBlockCategory === 'scorm') dispatch(actions.scorm.resetState())
    const baseComponent = {
      id: componentBlockId,
      category: componentBlockCategory,
      isNew: true,
    };

    const defaultsByCategory = {
      scorm: { metadata: {} },
      video: { metadata: {} },
      html: { data: '' },
      problem: { data: '' },
    };

    const newComponent = {
      ...baseComponent,
      ...(defaultsByCategory[componentBlockCategory] || {}),
    };

    setComponents(prev => [...prev, newComponent]);
    getComponents(true, componentBlockId)
    navigate(`/course/${courseId}/container/${unitId}/editor/${componentBlockCategory}/${componentBlockId}`);
  };

  const handleComponentUpdate = (updatedComponent) => {
    dispatch(actions.scorm.resetState())
    if(blockType === 'scorm' && blockId ){
      const component = components.find(val => val.id === blockId)
      if(component){
     dispatch(showToast({
            message: component?.isNew ? `${message[component?.category]} added successfully` : `${message[component?.category]} updated successfully`,
            status: ToastStatus.SUCCESSFUL
          }));
      }
      getComponents(true,blockId)
     return; 
    }
    setComponents((components) => components.map((component) => {
      if (component.id === updatedComponent.id) {
        getComponents(true)
        if (updatedComponent && message[component?.category]) {
          dispatch(showToast({
            message: component?.isNew ? `${message[component?.category]} added successfully` : `${message[component?.category]} updated successfully`,
            status: ToastStatus.SUCCESSFUL
          }));
        }
        return {
          ...component,
          ...updatedComponent,
          isNew: false,
        };
      }

      return component;
    }));
  };

  const getComponents = async (silent = false, newcomponentBlockId) => {
    if (unitId) {
      if (!silent) setLoading(true);
      getVerticalBlock(unitId).then(((response) => {
        setComponents(response.components.map(val => val?.id === newcomponentBlockId ? ({ ...val, isNew: true }) : val));
        setVerticleBlock(response.verticalBlock);
      })).finally(() => { setLoading(false); });
    }
  };
  useEffect(() => {
    getComponents();
  }, []);

  const publishLessonContent = async () => {
    await dispatch(publishCourseItemQuery(unitId, null, false, []))
    getComponents(true)
    dispatch(showToast({
      message: `Lesson published successfully`,
      status: ToastStatus.SUCCESSFUL
    }));
  }

  return (
    <Container size="xl" className="px-4 rounded p-4 container-screen w-fit overflow-hidden">
      <div className="bg-white _rounded-lg border border-light unit-xblocks-add-container overflow-hidden d-flex flex-column">
        {!loading && <div className="max-w-1200 bg-white d-flex justify-content-between align-items-center sub-header-container h-72 border-bottom border-bottom border-light">
          <h2 className="sub-header-title">{verticleBlock?.displayName}</h2>
          {verticleBlock && <div className='actions-btns'>
            <Button
              // iconBefore={Search}
              // data-testid="course-reindex"
              variant="outline-secondary"
              href={verticleBlock?.lmsUrl}
              target="_blank"
              size='sm'
              className='mr-3'

            >
              <SolidSvgComponent url={ViewIcon} width={16} height={16} defaultClass={`mr-1`} isIconColor /> {intl.formatMessage(messages.viewBtnText)}
            </Button>
            <Button
              type="button"
              onClick={publishLessonContent}
              data-testid="course-reindex"
              variant="outline-primary"
              disabled={!verticleBlock?.hasChanges || isShowProcessingNotification || loading}
              size='sm'
              loading={loading}
            >
              {intl.formatMessage(messages.saveBtnText)}
            </Button>
          </div>}
        </div>}
        <div className="max-w-1200 bg-white custom-scrollbar h-full overflow-auto pt-4" style={{ minHeight: '200px' }}>
          {loading && <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '150px' }}> <Spinner animation="border" variant="primary" className="mie-3" screenReaderText="loading" /> </div>}
          {!loading && components.length === 0 && <NoContent />}
          {!loading && components.length !== 0 && (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragOver={() => console.log('drag over')}
              onDragStart={(event) => console.log(event, 'drag start')}
              onDragEnd={handleDragEnd}
            >
              <SortableContext id="root" strategy={verticalListSortingStrategy} items={components}>
                {
                  components.map((component, i) => <DraggableComponent id={component.id} category={component.category} children={selectComponent(component)} isDraggable isDroppable style={i === 0 ? {} : { borderTop: "1px solid #0000001F", paddingTop: '1.5rem', }} />)
                }
              </SortableContext>
            </DndContext>
          )}
        </div>
        <div className="max-w-1200 _bg-gray-50 p-4 d-flex flex-column align-items-center justify-content-between _rounded-b-lg unit-xblock-add-component" style={{borderTop: "1px solid #0000001F"}}>
          <h2 className="sub-header-title">Add content</h2>
          <span className="text-gray-500 _font-weight-light mt-2">Please select the one of the below type</span>
          <div className="d-flex justify-content-between w-100 mt-4" style={{ gap: '1rem' }}>
            {
              [...availableComponents,...(isSCORMEnabled ? scormComponents: [])].map((component, index) => <AvailableComponentCard key={index} {...component} onSuccess={onSuccessComponentBlockCreate} />)
            }
          </div>
          {/* {
            isSCORMEnabled && <div className="justify-content-center w-100 mt-3 advancemodules-container" style={{ gap: '1rem' }}>
            {
              scormComponents.map((component, index) => <AvailableComponentCard key={index} {...component} onSuccess={onSuccessComponentBlockCreate} />)
            }
          </div>
          } */}
        </div>
      </div>
      <ProcessingNotification
        isShow={isShowProcessingNotification}
        title={processingNotificationTitle}
      />
      <DeleteModal
        category={intl.formatMessage(messages.deleteCategory, { category: "Block" })}
        isOpen={Boolean(isDeleteBlock?.id)}
        close={() => setDeleteBlock(null)}
        onDeleteSubmit={() => {
          handleDeleteBlock();
          setDeleteBlock(null);
        }}
        description={intl.formatMessage(messages.deleteDescription)}
        btnDefaultLabel={intl.formatMessage(messages.deleteSave)}
      />
      {toastMessage && (
        <Toast
          show
          onClose={/* istanbul ignore next */ () => setToastMessage(null)}
          data-testid="taxonomy-toast"
        >
          {toastMessage}
        </Toast>
      )}
      <UnitContextWrapper updateComponent={handleComponentUpdate} componentBlocks={components} handleDeleteComponentBlock={handleDeleteBlock}>
        <Outlet />
      </UnitContextWrapper>
    </Container>
  );
};

export default injectIntl(Unit);
